import { Plus, Trash2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

// ── Types ──────────────────────────────────────────────────────────────────

export type RuleOperator =
  | 'equals' | 'not_equals'
  | 'matches' | 'not_matches'
  | 'contains' | 'not_contains'
  | 'missing' | 'present'
  | 'gt' | 'lt' | 'gte' | 'lte'
  | 'in'

export interface PredicateNode {
  kind: 'predicate'
  fieldPath: string
  operator: RuleOperator
  value: string
}

export interface GroupNode {
  kind: 'group'
  groupType: 'all' | 'any'
  children: RuleNode[]
}

export interface NotNode {
  kind: 'not'
  child: RuleNode
}

export type RuleNode = PredicateNode | GroupNode | NotNode

// ── API 変換 ───────────────────────────────────────────────────────────────

export function toApiRule(node: RuleNode): unknown {
  if (node.kind === 'predicate') {
    const { fieldPath, operator, value } = node
    if (operator === 'missing' || operator === 'present') {
      return { [fieldPath]: { [operator]: true } }
    }
    if (operator === 'in') {
      return { [fieldPath]: { in: value.split(',').map(s => s.trim()).filter(Boolean) } }
    }
    if (operator === 'gt' || operator === 'lt' || operator === 'gte' || operator === 'lte') {
      return { [fieldPath]: { [operator]: Number(value) } }
    }
    return { [fieldPath]: { [operator]: value } }
  }
  if (node.kind === 'group') {
    return { [node.groupType]: node.children.map(toApiRule) }
  }
  return { not: toApiRule(node.child) }
}

// ── API ↔ RuleNode 変換 ────────────────────────────────────────────────────

export function fromApiRule(api: unknown): RuleNode {
  if (!api || typeof api !== 'object') return defaultGroupNode()
  const obj = api as Record<string, unknown>

  if ('all' in obj && Array.isArray(obj.all)) {
    return { kind: 'group', groupType: 'all', children: (obj.all as unknown[]).map(fromApiRule) }
  }
  if ('any' in obj && Array.isArray(obj.any)) {
    return { kind: 'group', groupType: 'any', children: (obj.any as unknown[]).map(fromApiRule) }
  }
  if ('not' in obj) {
    return { kind: 'not', child: fromApiRule(obj.not) }
  }

  const keys = Object.keys(obj)
  if (keys.length === 1) {
    const fieldPath = keys[0]
    const inner = obj[fieldPath]
    if (inner && typeof inner === 'object') {
      const innerObj = inner as Record<string, unknown>
      const op = Object.keys(innerObj)[0] as RuleOperator
      const val = innerObj[op]
      if (op === 'missing' || op === 'present') {
        return { kind: 'predicate', fieldPath, operator: op, value: '' }
      }
      if (op === 'in' && Array.isArray(val)) {
        return { kind: 'predicate', fieldPath, operator: 'in', value: (val as unknown[]).map(String).join(', ') }
      }
      return { kind: 'predicate', fieldPath, operator: op, value: String(val ?? '') }
    }
  }

  return defaultGroupNode()
}

// ── デフォルト値 ────────────────────────────────────────────────────────────

export function defaultPredicateNode(): PredicateNode {
  return { kind: 'predicate', fieldPath: 'header.authorization', operator: 'missing', value: '' }
}

export function defaultGroupNode(groupType: 'all' | 'any' = 'all'): GroupNode {
  return { kind: 'group', groupType, children: [defaultPredicateNode()] }
}

// ── 定数 ──────────────────────────────────────────────────────────────────

const FIELD_PRESETS = [
  'header.authorization',
  'header.content-type',
  'header.x-api-key',
  'header.x-user-id',
  'query.debug',
  'query.version',
  'body.id',
  'body.status',
  'method',
]

type ValueType = 'text' | 'number' | 'none' | 'csv'

const OPERATORS: { value: RuleOperator; label: string; valueType: ValueType }[] = [
  { value: 'equals',       label: 'equals',           valueType: 'text'   },
  { value: 'not_equals',   label: 'not equals',       valueType: 'text'   },
  { value: 'matches',      label: 'matches (regex)',   valueType: 'text'   },
  { value: 'not_matches',  label: 'not matches',      valueType: 'text'   },
  { value: 'contains',     label: 'contains',         valueType: 'text'   },
  { value: 'not_contains', label: 'not contains',     valueType: 'text'   },
  { value: 'missing',      label: 'is missing',       valueType: 'none'   },
  { value: 'present',      label: 'is present',       valueType: 'none'   },
  { value: 'gt',           label: '> (greater than)', valueType: 'number' },
  { value: 'lt',           label: '< (less than)',    valueType: 'number' },
  { value: 'gte',          label: '≥ (gte)',           valueType: 'number' },
  { value: 'lte',          label: '≤ (lte)',           valueType: 'number' },
  { value: 'in',           label: 'in (list)',         valueType: 'csv'    },
]

function getValueType(op: RuleOperator): ValueType {
  return OPERATORS.find(o => o.value === op)?.valueType ?? 'text'
}

const MAX_DEPTH = 4

// ── RuleBuilder ────────────────────────────────────────────────────────────

export interface RuleBuilderProps {
  node: RuleNode
  onChange: (node: RuleNode) => void
  onRemove?: () => void
  depth?: number
}

export function RuleBuilder({ node, onChange, onRemove, depth = 0 }: RuleBuilderProps) {
  const canNest = depth < MAX_DEPTH
  const { t } = useTranslation()

  // ── Predicate ──
  if (node.kind === 'predicate') {
    const vt = getValueType(node.operator)
    return (
      <div className="flex flex-wrap items-start gap-1.5 rounded border bg-background p-2">
        {/* Node type selector */}
        <Select
          value="predicate"
          onValueChange={(v) => {
            if (v === 'all') onChange(defaultGroupNode('all'))
            else if (v === 'any') onChange(defaultGroupNode('any'))
            else if (v === 'not') onChange({ kind: 'not', child: node })
          }}
        >
          <SelectTrigger className="h-7 w-[86px] text-xs shrink-0">
            <SelectValue>{t('ruleBuilder.condition')}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="predicate">{t('ruleBuilder.condition')}</SelectItem>
            {canNest && <SelectItem value="all">{t('ruleBuilder.allGroup')}</SelectItem>}
            {canNest && <SelectItem value="any">{t('ruleBuilder.anyGroup')}</SelectItem>}
            {canNest && <SelectItem value="not">NOT</SelectItem>}
          </SelectContent>
        </Select>

        {/* フィールドパス (プリセット候補付き) */}
        <div className="flex-1 min-w-[140px]">
          <input
            list="rule-field-presets"
            className="flex h-7 w-full rounded-md border border-input bg-background px-3 py-1 text-xs font-mono shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            placeholder="header.authorization"
            value={node.fieldPath}
            onChange={e => onChange({ ...node, fieldPath: e.target.value })}
          />
          <datalist id="rule-field-presets">
            {FIELD_PRESETS.map(p => <option key={p} value={p} />)}
          </datalist>
        </div>

        {/* 演算子 */}
        <Select
          value={node.operator}
          onValueChange={v => onChange({ ...node, operator: v as RuleOperator, value: '' })}
        >
          <SelectTrigger className="h-7 w-[160px] text-xs shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OPERATORS.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Value input (shown based on operator type) */}
        {vt !== 'none' && (
          <div className="flex-1 min-w-[100px]">
            <input
              type={vt === 'number' ? 'number' : 'text'}
              className="flex h-7 w-full rounded-md border border-input bg-background px-3 py-1 text-xs shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder={vt === 'csv' ? 'a, b, c' : t('ruleBuilder.value')}
              value={node.value}
              onChange={e => onChange({ ...node, value: e.target.value })}
            />
          </div>
        )}

        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    )
  }

  // ── NOT ──
  if (node.kind === 'not') {
    return (
      <div className="rounded border border-orange-200 bg-orange-50/50 p-2 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-orange-600 px-1">NOT</span>
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={onRemove}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <div className="pl-3 border-l-2 border-orange-200">
          <RuleBuilder
            node={node.child}
            onChange={(child) => onChange({ ...node, child })}
            depth={depth + 1}
          />
        </div>
      </div>
    )
  }

  // ── Group (all / any) ──
  const isAll = node.groupType === 'all'
  const borderColor = isAll ? '#93c5fd' : '#86efac'

  const updateChild = (i: number, child: RuleNode) => {
    const children = [...node.children]
    children[i] = child
    onChange({ ...node, children })
  }

  const removeChild = (i: number) => {
    onChange({ ...node, children: node.children.filter((_, idx) => idx !== i) })
  }

  const addChild = (kind: 'predicate' | 'all' | 'any' | 'not') => {
    let newChild: RuleNode
    if (kind === 'predicate') newChild = defaultPredicateNode()
    else if (kind === 'not') newChild = { kind: 'not', child: defaultPredicateNode() }
    else newChild = defaultGroupNode(kind)
    onChange({ ...node, children: [...node.children, newChild] })
  }

  return (
    <div className={cn(
      'rounded border p-2 space-y-2',
      isAll ? 'bg-blue-50/50 border-blue-200' : 'bg-green-50/50 border-green-200',
    )}>
      {/* グループヘッダー */}
      <div className="flex items-center gap-2">
        <Select
          value={node.groupType}
          onValueChange={(v) => onChange({ ...node, groupType: v as 'all' | 'any' })}
        >
          <SelectTrigger className={cn(
            'h-7 w-[72px] text-xs font-bold shrink-0',
            isAll ? 'text-blue-700' : 'text-green-700',
          )}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">ALL</SelectItem>
            <SelectItem value="any">ANY</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          {isAll ? t('ruleBuilder.allConditions') : t('ruleBuilder.anyConditions')}
        </span>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="ml-auto h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* 子ノード */}
      <div className="space-y-2 pl-3" style={{ borderLeft: `2px solid ${borderColor}` }}>
        {node.children.map((child, i) => (
          <RuleBuilder
            key={i}
            node={child}
            onChange={(updated) => updateChild(i, updated)}
            onRemove={node.children.length > 1 ? () => removeChild(i) : undefined}
            depth={depth + 1}
          />
        ))}
      </div>

      {/* Child add buttons */}
      {canNest && (
        <div className="flex flex-wrap gap-1">
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addChild('predicate')}>
            <Plus className="h-3 w-3 mr-1" />{t('ruleBuilder.addCondition')}
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addChild('all')}>
            <Plus className="h-3 w-3 mr-1" />ALL
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addChild('any')}>
            <Plus className="h-3 w-3 mr-1" />ANY
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => addChild('not')}>
            <Plus className="h-3 w-3 mr-1" />NOT
          </Button>
        </div>
      )}
    </div>
  )
}
